// src/pages/DCFleet.jsx
// Route: /dc-fleet
// Register in App.jsx: <Route path="/dc-fleet" element={<DCFleet />} />
// Add to Sidebar.jsx under "Vertical Solutions" alongside DC Sentry
//
// Purpose: Multi-site DC fleet overview for CISO / infrastructure director.
// Shows every data center site as a card: paired OT + OOB agent health,
// asset breakdown, alert counts, active correlations, site risk score.
// Clicking a site navigates to /project-oracle with that site pre-selected.
//
// Mock data: Aligned Data Centers portfolio (US + LATAM via ODATA).
// When real DC_ agents are registered, mock data is replaced automatically.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { edgeNodesService } from '../services/edge-nodes';
import { eventsService } from '../services/events';

// ════════════════════════════════════════════════════════════════════════════
// ALIGNED DATA CENTERS — MOCK SITE PORTFOLIO
// Real locations, varied health states to demonstrate fleet view value.
// Replaced by live data once DC_ agents are registered.
// ════════════════════════════════════════════════════════════════════════════

const now = Date.now();
const minsAgo  = (m) => new Date(now - m * 60000).toISOString();
const hrsAgo   = (h) => new Date(now - h * 3600000).toISOString();
const daysAgo  = (d) => new Date(now - d * 86400000).toISOString();

const MOCK_ALIGNED_SITES = [
  // ── US Campuses ──────────────────────────────────────────────────────────

  {
    // HQ market. Live POV. CORR-001 active — unauthorized physical access
    // correlated with IPMI login attempt on same rack row.
    name: 'DALLAS',
    city: 'Dallas / Plano, TX',
    campusCode: 'DAL-01',
    region: 'US',
    otAgent:  { edge_node_id: 'DC_OT_DALLAS_01',  hostname: 'dal-ot-edge-01',  last_heartbeat: minsAgo(2)  },
    oobAgent: { edge_node_id: 'DC_OOB_DALLAS_01', hostname: 'dal-oob-edge-01', last_heartbeat: minsAgo(1)  },
    mockEvents: [
      { severity: 'Critical', protocol: 'IPMI',   description: 'IPMI auth failure — 7 attempts in 60s on BMC 192.168.100.11', timestamp: minsAgo(3)  },
      { severity: 'Critical', protocol: 'BACnet', description: 'BACnet WriteProperty to CRAC setpoint — unauthorized source',  timestamp: minsAgo(8)  },
      { severity: 'Warning',  protocol: 'SNMP',   description: 'SNMP SET with default community string "public"',              timestamp: minsAgo(22) },
    ],
    mockCorrelations: [
      { severity: 'Critical', details: { correlation_rule_id: 'CORR-001', correlation_rule_name: 'Physical Intrusion + BMC Activity', confidence: 95, description: 'Unauthorized person detected in Server Hall A at 14:32. IPMI login attempt on rack row 4 BMC at 14:35. Physical + digital intent confirmed.', fired_at: minsAgo(5) } },
    ],
    assetProfile: { otCount: 6, oobCount: 14, highRisk: 5, riskScore: 91, riskLevel: 'Critical' },
  },

  {
    // Northern Virginia. Largest US campus. Active warnings, healthy agents.
    name: 'ASHBURN',
    city: 'Ashburn / Sterling, VA',
    campusCode: 'IAD-01',
    region: 'US',
    otAgent:  { edge_node_id: 'DC_OT_ASHBURN_01',  hostname: 'iad-ot-edge-01',  last_heartbeat: minsAgo(4)  },
    oobAgent: { edge_node_id: 'DC_OOB_ASHBURN_01', hostname: 'iad-oob-edge-01', last_heartbeat: minsAgo(3)  },
    mockEvents: [
      { severity: 'Warning',  protocol: 'Redfish', description: 'Redfish firmware update endpoint called outside maintenance window', timestamp: minsAgo(18) },
      { severity: 'Warning',  protocol: 'Modbus',  description: 'Modbus WRITE FC16 to UPS — source not in authorized whitelist',      timestamp: minsAgo(45) },
      { severity: 'Warning',  protocol: 'IPMI',    description: 'IPMI v1.5 detected — insecure protocol version on BMC 10.0.1.22',     timestamp: hrsAgo(2)  },
    ],
    mockCorrelations: [],
    assetProfile: { otCount: 8, oobCount: 22, highRisk: 3, riskScore: 67, riskLevel: 'High' },
  },

  {
    // Phoenix. Clean. Reference healthy site for contrast.
    name: 'PHOENIX',
    city: 'Phoenix, AZ',
    campusCode: 'PHX-04',
    region: 'US',
    otAgent:  { edge_node_id: 'DC_OT_PHOENIX_01',  hostname: 'phx-ot-edge-01',  last_heartbeat: minsAgo(6)  },
    oobAgent: { edge_node_id: 'DC_OOB_PHOENIX_01', hostname: 'phx-oob-edge-01', last_heartbeat: minsAgo(5)  },
    mockEvents: [
      { severity: 'Warning', protocol: 'SNMP', description: 'SNMP GET sweep from unknown host — possible asset enumeration', timestamp: hrsAgo(6) },
    ],
    mockCorrelations: [],
    assetProfile: { otCount: 5, oobCount: 11, highRisk: 1, riskScore: 44, riskLevel: 'Medium' },
  },

  {
    // Chicago. OOB agent offline — management network blind spot.
    name: 'CHICAGO',
    city: 'Northlake, IL',
    campusCode: 'ORD-02',
    region: 'US',
    otAgent:  { edge_node_id: 'DC_OT_CHICAGO_01',  hostname: 'ord-ot-edge-01',  last_heartbeat: minsAgo(7)  },
    oobAgent: { edge_node_id: 'DC_OOB_CHICAGO_01', hostname: 'ord-oob-edge-01', last_heartbeat: hrsAgo(4)   }, // OFFLINE
    mockEvents: [
      { severity: 'Warning', protocol: 'BACnet', description: 'BACnet WhoIs broadcast from unknown IP — passive recon pattern', timestamp: hrsAgo(1) },
      { severity: 'Warning', protocol: 'Modbus', description: 'Modbus READ FC3 — high frequency polling from external IP',       timestamp: hrsAgo(3) },
    ],
    mockCorrelations: [],
    assetProfile: { otCount: 7, oobCount: 18, highRisk: 2, riskScore: 58, riskLevel: 'High' },
  },

  {
    // Salt Lake City. Clean and healthy.
    name: 'SALTLAKE',
    city: 'West Jordan / West Valley, UT',
    campusCode: 'SLC-02',
    region: 'US',
    otAgent:  { edge_node_id: 'DC_OT_SALTLAKE_01',  hostname: 'slc-ot-edge-01',  last_heartbeat: minsAgo(9)  },
    oobAgent: { edge_node_id: 'DC_OOB_SALTLAKE_01', hostname: 'slc-oob-edge-01', last_heartbeat: minsAgo(8)  },
    mockEvents: [],
    mockCorrelations: [],
    assetProfile: { otCount: 4, oobCount: 9, highRisk: 0, riskScore: 32, riskLevel: 'Low' },
  },

  {
    // Frederick, MD. New campus under construction. OOB agent not yet deployed.
    name: 'FREDERICK',
    city: 'Frederick, MD',
    campusCode: 'IAD-06',
    region: 'US',
    otAgent:  { edge_node_id: 'DC_OT_FREDERICK_01', hostname: 'mdy-ot-edge-01', last_heartbeat: minsAgo(11) },
    oobAgent: null, // OOB not yet deployed
    mockEvents: [
      { severity: 'Warning', protocol: 'Modbus', description: 'Modbus WRITE FC5 — coil write on PDU during non-maintenance window', timestamp: hrsAgo(1) },
    ],
    mockCorrelations: [],
    assetProfile: { otCount: 4, oobCount: 0, highRisk: 1, riskScore: 52, riskLevel: 'Medium' },
  },

  {
    // Portland. Clean.
    name: 'PORTLAND',
    city: 'Portland, OR',
    campusCode: 'PDX-01',
    region: 'US',
    otAgent:  { edge_node_id: 'DC_OT_PORTLAND_01',  hostname: 'pdx-ot-edge-01',  last_heartbeat: minsAgo(5)  },
    oobAgent: { edge_node_id: 'DC_OOB_PORTLAND_01', hostname: 'pdx-oob-edge-01', last_heartbeat: minsAgo(4)  },
    mockEvents: [],
    mockCorrelations: [],
    assetProfile: { otCount: 3, oobCount: 7, highRisk: 0, riskScore: 28, riskLevel: 'Low' },
  },

  // ── LATAM via ODATA ──────────────────────────────────────────────────────

  {
    // São Paulo. Largest LATAM site. CORR-002 active.
    name: 'SAOPAULO',
    city: 'São Paulo / Hortolândia, BR',
    campusCode: 'SP-07',
    region: 'LATAM',
    otAgent:  { edge_node_id: 'DC_OT_SAOPAULO_01',  hostname: 'gru-ot-edge-01',  last_heartbeat: minsAgo(3)  },
    oobAgent: { edge_node_id: 'DC_OOB_SAOPAULO_01', hostname: 'gru-oob-edge-01', last_heartbeat: minsAgo(2)  },
    mockEvents: [
      { severity: 'Critical', protocol: 'IPMI',    description: 'IPMI chassis power-cycle command from unrecognized source IP',  timestamp: minsAgo(12) },
      { severity: 'Critical', protocol: 'Modbus',  description: 'Modbus WRITE to PDU power register — unauthorized source',       timestamp: minsAgo(14) },
      { severity: 'Warning',  protocol: 'Redfish', description: 'Redfish /Systems PATCH request outside maintenance window',      timestamp: minsAgo(30) },
    ],
    mockCorrelations: [
      { severity: 'Critical', details: { correlation_rule_id: 'CORR-002', correlation_rule_name: 'PDU Power Cycle + BMC Command', confidence: 90, description: 'PDU power register write on rack B-07 at 14:22. IPMI chassis power-cycle on same rack 90 seconds later. Coordinated physical + management plane attack pattern.', fired_at: minsAgo(12) } },
    ],
    assetProfile: { otCount: 7, oobCount: 16, highRisk: 4, riskScore: 88, riskLevel: 'Critical' },
  },

  {
    // Bogotá. OT agent offline — facilities network dark.
    name: 'BOGOTA',
    city: 'Mosquera / Bogotá, CO',
    campusCode: 'BG-02',
    region: 'LATAM',
    otAgent:  { edge_node_id: 'DC_OT_BOGOTA_01',  hostname: 'bog-ot-edge-01',  last_heartbeat: hrsAgo(6)  }, // OFFLINE
    oobAgent: { edge_node_id: 'DC_OOB_BOGOTA_01', hostname: 'bog-oob-edge-01', last_heartbeat: minsAgo(8) },
    mockEvents: [
      { severity: 'Warning', protocol: 'IPMI', description: 'IPMI v1.5 — 3 BMCs on management VLAN using insecure protocol version', timestamp: hrsAgo(2) },
    ],
    mockCorrelations: [],
    assetProfile: { otCount: 4, oobCount: 8, highRisk: 2, riskScore: 61, riskLevel: 'High' },
  },

  {
    // Querétaro, Mexico. Clean.
    name: 'QUERETARO',
    city: 'Querétaro, MX',
    campusCode: 'QR-03',
    region: 'LATAM',
    otAgent:  { edge_node_id: 'DC_OT_QUERETARO_01',  hostname: 'qro-ot-edge-01',  last_heartbeat: minsAgo(10) },
    oobAgent: { edge_node_id: 'DC_OOB_QUERETARO_01', hostname: 'qro-oob-edge-01', last_heartbeat: minsAgo(9)  },
    mockEvents: [],
    mockCorrelations: [],
    assetProfile: { otCount: 3, oobCount: 6, highRisk: 0, riskScore: 29, riskLevel: 'Low' },
  },

  {
    // Santiago, Chile. Minor legacy config warning.
    name: 'SANTIAGO',
    city: 'Lampa / Santiago, CL',
    campusCode: 'SCL-01',
    region: 'LATAM',
    otAgent:  { edge_node_id: 'DC_OT_SANTIAGO_01',  hostname: 'scl-ot-edge-01',  last_heartbeat: minsAgo(6)  },
    oobAgent: { edge_node_id: 'DC_OOB_SANTIAGO_01', hostname: 'scl-oob-edge-01', last_heartbeat: minsAgo(5)  },
    mockEvents: [
      { severity: 'Warning', protocol: 'SNMP', description: 'SNMP v1 community string "private" detected — legacy config on UPS', timestamp: hrsAgo(4) },
    ],
    mockCorrelations: [],
    assetProfile: { otCount: 3, oobCount: 7, highRisk: 1, riskScore: 41, riskLevel: 'Medium' },
  },

  {
    // Buenos Aires. Both agents offline — site connectivity issue.
    name: 'BUENOSAIRES',
    city: 'Buenos Aires, AR',
    campusCode: 'EZE-01',
    region: 'LATAM',
    otAgent:  { edge_node_id: 'DC_OT_BUENOSAIRES_01',  hostname: 'eze-ot-edge-01',  last_heartbeat: daysAgo(1) }, // OFFLINE
    oobAgent: { edge_node_id: 'DC_OOB_BUENOSAIRES_01', hostname: 'eze-oob-edge-01', last_heartbeat: daysAgo(1) }, // OFFLINE
    mockEvents: [],
    mockCorrelations: [],
    assetProfile: { otCount: 3, oobCount: 6, highRisk: 0, riskScore: 55, riskLevel: 'Medium' },
  },

  {
    // Lima, Peru. Healthy.
    name: 'LIMA',
    city: 'Lurín / Lima, PE',
    campusCode: 'LIM-01',
    region: 'LATAM',
    otAgent:  { edge_node_id: 'DC_OT_LIMA_01',  hostname: 'lim-ot-edge-01',  last_heartbeat: minsAgo(7)  },
    oobAgent: { edge_node_id: 'DC_OOB_LIMA_01', hostname: 'lim-oob-edge-01', last_heartbeat: minsAgo(6)  },
    mockEvents: [],
    mockCorrelations: [],
    assetProfile: { otCount: 3, oobCount: 5, highRisk: 0, riskScore: 27, riskLevel: 'Low' },
  },
];

// Convert mock site definitions into the enriched format the fleet UI consumes
const buildMockSiteData = (mockSite) => {
  const otOnline  = mockSite.otAgent  ? isNodeOnline(mockSite.otAgent)  : false;
  const oobOnline = mockSite.oobAgent ? isNodeOnline(mockSite.oobAgent) : false;

  const criticalCount = mockSite.mockEvents.filter(e => e.severity === 'Critical').length;
  const warningCount  = mockSite.mockEvents.filter(e => e.severity === 'Warning').length;
  const totalAlerts   = criticalCount + warningCount;
  const corrCount     = mockSite.mockCorrelations.length;

  let siteStatus;
  if (!mockSite.otAgent && !mockSite.oobAgent)                      siteStatus = 'unconfigured';
  else if (!otOnline && !oobOnline)                                  siteStatus = 'offline';
  else if ((!otOnline && mockSite.otAgent) || (!oobOnline && mockSite.oobAgent && !otOnline)) siteStatus = 'offline';
  else if ((mockSite.otAgent && !otOnline) || (mockSite.oobAgent && !oobOnline)) siteStatus = 'degraded';
  else if (corrCount > 0)                                            siteStatus = 'correlation';
  else if (criticalCount > 0)                                        siteStatus = 'critical';
  else if (warningCount > 0)                                         siteStatus = 'warning';
  else                                                               siteStatus = 'healthy';

  const lastEvent = mockSite.mockEvents.length > 0
    ? [...mockSite.mockEvents].sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
    : null;

  const protocols = [...new Set(mockSite.mockEvents.map(e => e.protocol).filter(Boolean))];

  return {
    name: mockSite.name,
    city: mockSite.city,
    campusCode: mockSite.campusCode,
    region: mockSite.region,
    otAgent:  mockSite.otAgent,
    oobAgent: mockSite.oobAgent,
    otOnline,
    oobOnline,
    siteStatus,
    criticalCount,
    warningCount,
    totalAlerts,
    correlationCount: corrCount,
    correlations: mockSite.mockCorrelations,
    otAssetCount:   mockSite.assetProfile.otCount,
    oobAssetCount:  mockSite.assetProfile.oobCount,
    totalAssets:    mockSite.assetProfile.otCount + mockSite.assetProfile.oobCount,
    highRiskAssets: mockSite.assetProfile.highRisk,
    riskScore:      mockSite.assetProfile.riskScore,
    riskLevel:      mockSite.assetProfile.riskLevel,
    totalEvents:    mockSite.mockEvents.length,
    lastEvent,
    protocols,
    otLastHeartbeat:  lastHeartbeatAge(mockSite.otAgent),
    oobLastHeartbeat: lastHeartbeatAge(mockSite.oobAgent),
  };
};

// ════════════════════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════════════════════

const parseDcNodeId = (id = '') => {
  if (!id.toUpperCase().startsWith('DC_')) return null;
  const parts = id.toUpperCase().split('_');
  if (parts.length >= 4) return { plane: parts[1].toLowerCase(), site: parts.slice(2, parts.length - 1).join('_') };
  if (parts.length === 3) return { plane: 'ot', site: parts[1] };
  return null;
};

const groupBySite = (edgeNodes) => {
  const sites = {};
  edgeNodes.forEach(node => {
    const parsed = parseDcNodeId(node.edge_node_id);
    if (!parsed) return;
    const { site, plane } = parsed;
    if (!sites[site]) sites[site] = { name: site, otAgent: null, oobAgent: null };
    if (plane === 'oob') sites[site].oobAgent = node;
    else sites[site].otAgent = node;
  });
  return sites;
};

function isNodeOnline(node) {
  if (!node?.last_heartbeat) return false;
  let ts = node.last_heartbeat;
  if (!ts.endsWith('Z') && !ts.includes('+')) ts += 'Z';
  return (Date.now() - new Date(ts).getTime()) / 60000 <= 15;
}

function lastHeartbeatAge(node) {
  if (!node?.last_heartbeat) return null;
  let ts = node.last_heartbeat;
  if (!ts.endsWith('Z') && !ts.includes('+')) ts += 'Z';
  const diffMs = Date.now() - new Date(ts).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return `${Math.floor(diffHrs / 24)}d ago`;
}

const buildLiveSiteData = (site, events, correlationEvents) => {
  const agentIds = new Set();
  if (site.otAgent)  agentIds.add(site.otAgent.edge_node_id);
  if (site.oobAgent) agentIds.add(site.oobAgent.edge_node_id);

  const siteEvents       = events.filter(e => agentIds.has(e.celerway_edge_node_id));
  const siteCorrelations = correlationEvents.filter(e =>
    agentIds.has(e.celerway_edge_node_id) ||
    Array.from(agentIds).some(id => JSON.stringify(e.details || {}).includes(id))
  );

  const criticalCount = siteEvents.filter(e => e.severity === 'Critical').length;
  const warningCount  = siteEvents.filter(e => e.severity === 'Warning').length;
  const corrCount     = siteCorrelations.length;
  const otOnline      = site.otAgent  ? isNodeOnline(site.otAgent)  : false;
  const oobOnline     = site.oobAgent ? isNodeOnline(site.oobAgent) : false;

  let siteStatus;
  if (!site.otAgent && !site.oobAgent) siteStatus = 'unconfigured';
  else if (!otOnline && !oobOnline)    siteStatus = 'offline';
  else if (!otOnline || !oobOnline)    siteStatus = 'degraded';
  else if (corrCount > 0)              siteStatus = 'correlation';
  else if (criticalCount > 0)          siteStatus = 'critical';
  else if (warningCount > 0)           siteStatus = 'warning';
  else                                 siteStatus = 'healthy';

  const alertPenalty = Math.min(20, criticalCount * 5 + warningCount * 2);
  const riskScore    = Math.min(100, Math.round(50 + alertPenalty));
  const riskLevel    = riskScore >= 80 ? 'Critical' : riskScore >= 60 ? 'High' : riskScore >= 40 ? 'Medium' : 'Low';
  const lastEvent    = [...siteEvents].sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
  const protocols    = [...new Set(siteEvents.map(e => e.protocol).filter(Boolean))].slice(0, 5);

  return {
    name: site.name, city: site.name, campusCode: '', region: 'Live',
    otAgent: site.otAgent, oobAgent: site.oobAgent, otOnline, oobOnline, siteStatus,
    criticalCount, warningCount, totalAlerts: criticalCount + warningCount,
    correlationCount: corrCount, correlations: siteCorrelations,
    otAssetCount: 0, oobAssetCount: 0, totalAssets: 0, highRiskAssets: 0,
    riskScore, riskLevel, totalEvents: siteEvents.length, lastEvent, protocols,
    otLastHeartbeat:  lastHeartbeatAge(site.otAgent),
    oobLastHeartbeat: lastHeartbeatAge(site.oobAgent),
  };
};

// ════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

const AgentPill = ({ label, online, lastSeen, colorClass, notDeployed }) => {
  if (notDeployed) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border bg-slate-50 border-slate-200">
        <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
        <span className="text-xs font-bold text-slate-400">{label}</span>
        <span className="text-[10px] text-slate-400">Not deployed</span>
      </div>
    );
  }
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${online ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-emerald-500 animate-pulse' : 'bg-red-400'}`} />
      <span className={`text-xs font-bold ${colorClass}`}>{label}</span>
      <span className={`text-[10px] ${online ? 'text-emerald-600' : 'text-slate-400'}`}>{online ? 'Live' : (lastSeen || 'Offline')}</span>
    </div>
  );
};

const RiskBadge = ({ score, level }) => {
  const styles = { Critical: 'bg-red-100 text-red-700 border-red-300', High: 'bg-amber-100 text-amber-700 border-amber-300', Medium: 'bg-yellow-100 text-yellow-700 border-yellow-300', Low: 'bg-emerald-100 text-emerald-700 border-emerald-300' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${styles[level] || styles.Medium}`}>{score} · {level}</span>;
};

const StatusBadge = ({ status }) => {
  const config = {
    healthy:      { label: 'Healthy',      bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300', dot: 'bg-emerald-500', pulse: true  },
    warning:      { label: 'Warning',      bg: 'bg-yellow-100',  text: 'text-yellow-700',  border: 'border-yellow-300',  dot: 'bg-yellow-500',  pulse: false },
    critical:     { label: 'Critical',     bg: 'bg-red-100',     text: 'text-red-700',     border: 'border-red-300',     dot: 'bg-red-500',     pulse: true  },
    correlation:  { label: 'Correlation',  bg: 'bg-orange-100',  text: 'text-orange-700',  border: 'border-orange-300',  dot: 'bg-orange-500',  pulse: true  },
    degraded:     { label: 'Degraded',     bg: 'bg-amber-100',   text: 'text-amber-700',   border: 'border-amber-300',   dot: 'bg-amber-500',   pulse: false },
    offline:      { label: 'Offline',      bg: 'bg-slate-100',   text: 'text-slate-600',   border: 'border-slate-300',   dot: 'bg-slate-400',   pulse: false },
    unconfigured: { label: 'Unconfigured', bg: 'bg-slate-100',   text: 'text-slate-400',   border: 'border-slate-200',   dot: 'bg-slate-300',   pulse: false },
  };
  const c = config[status] || config.offline;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${c.bg} ${c.text} ${c.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot} ${c.pulse ? 'animate-pulse' : ''}`} />
      {c.label}
    </span>
  );
};

const scoreColor = (score) => {
  if (score === null || score === undefined) return 'text-slate-400';
  if (score >= 75) return 'text-red-600';
  if (score >= 50) return 'text-amber-600';
  if (score >= 25) return 'text-yellow-600';
  return 'text-emerald-600';
};

const scoreBg = (score) => {
  if (score === null || score === undefined) return 'bg-slate-50 border-slate-200';
  if (score >= 75) return 'bg-red-50 border-red-200';
  if (score >= 50) return 'bg-amber-50 border-amber-200';
  if (score >= 25) return 'bg-yellow-50 border-yellow-200';
  return 'bg-emerald-50 border-emerald-200';
};

const Corr004Panel = ({ siteName, corr004Data, loading }) => {
  const siteData = corr004Data?.[siteName] || {};
  const qd = siteData['queue_delay'];
  const sr = siteData['site_risk'];
  const pr = siteData['procurement_risk'];

  if (loading) {
    return (
      <div className="mx-4 mb-2.5 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg animate-pulse">
        <div className="h-3 bg-slate-200 rounded w-24 mb-1" />
        <div className="h-2 bg-slate-100 rounded w-full" />
      </div>
    );
  }

  if (!qd && !sr && !pr) {
    return (
      <div className="mx-4 mb-2.5 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
        <div className="text-[9px] text-slate-400 font-semibold uppercase mb-1">
          CORR-004 Intelligence
        </div>
        <div className="text-[9px] text-slate-400 italic">
          No data — outside US coverage or pending ingest
        </div>
      </div>
    );
  }

  return (
    <div className="mx-4 mb-2.5 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg">
      <div className="text-[9px] text-slate-500 font-semibold uppercase mb-2 flex items-center gap-1">
        CORR-004 Site Intelligence
        {qd?.confidence === 'high' && (
          <span className="px-1 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[8px] border border-emerald-200">
            High Conf
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {/* Queue Delay */}
        <div className={`rounded-lg border px-2 py-1.5 ${qd ? scoreBg(qd.score) : 'bg-slate-50 border-slate-200'}`}>
          <div className="text-[8px] text-slate-500 uppercase font-semibold mb-0.5">
            Queue Delay
          </div>
          <div className={`text-sm font-bold ${qd ? scoreColor(qd.score) : 'text-slate-300'}`}>
            {qd ? `${Math.round(qd.score)}` : '\u2014'}
            <span className="text-[9px] font-normal">/100</span>
          </div>
          <div className="text-[8px] text-slate-400 mt-0.5 truncate">
            {qd?.confidence || 'no data'}
          </div>
        </div>

        {/* Site Risk */}
        <div className={`rounded-lg border px-2 py-1.5 ${sr ? scoreBg(sr.score) : 'bg-slate-50 border-slate-200'}`}>
          <div className="text-[8px] text-slate-500 uppercase font-semibold mb-0.5">
            Site Risk
          </div>
          <div className={`text-sm font-bold ${sr ? scoreColor(sr.score) : 'text-slate-300'}`}>
            {sr ? `${Math.round(sr.score)}` : '\u2014'}
            <span className="text-[9px] font-normal">/100</span>
          </div>
          <div className="text-[8px] text-slate-400 mt-0.5 truncate">
            {sr?.confidence || 'no data'}
          </div>
        </div>

        {/* Procurement Risk */}
        <div className={`rounded-lg border px-2 py-1.5 ${pr ? scoreBg(pr.score) : 'bg-slate-50 border-slate-200'}`}>
          <div className="text-[8px] text-slate-500 uppercase font-semibold mb-0.5">
            Procurement
          </div>
          <div className={`text-sm font-bold ${pr ? scoreColor(pr.score) : 'text-slate-300'}`}>
            {pr ? `${Math.round(pr.score)}` : '\u2014'}
            <span className="text-[9px] font-normal">/100</span>
          </div>
          <div className="text-[8px] text-slate-400 mt-0.5 truncate">
            {pr?.confidence || 'no data'}
          </div>
        </div>
      </div>

      {/* High risk callout */}
      {qd && qd.score >= 75 && (
        <div className="mt-1.5 text-[8px] text-red-600 font-semibold flex items-center gap-1">
          High interconnection slip risk — engage ISO early
        </div>
      )}
      {qd && qd.score >= 50 && qd.score < 75 && (
        <div className="mt-1.5 text-[8px] text-amber-600 font-semibold flex items-center gap-1">
          Moderate queue delay risk — build timeline buffer
        </div>
      )}
    </div>
  );
};

const RiskBar = ({ score }) => {
  const color = score >= 80 ? 'bg-red-500' : score >= 60 ? 'bg-amber-500' : score >= 40 ? 'bg-yellow-500' : 'bg-emerald-500';
  return (
    <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
    </div>
  );
};

const RegionTag = ({ region }) => {
  const styles = { US: 'bg-blue-50 text-blue-600 border-blue-200', LATAM: 'bg-green-50 text-green-600 border-green-200', Live: 'bg-cyan-50 text-cyan-600 border-cyan-200' };
  return <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded border ${styles[region] || styles.Live}`}>{region}</span>;
};

// ════════════════════════════════════════════════════════════════════════════
// SITE CARD
// ════════════════════════════════════════════════════════════════════════════

const SiteCard = ({ site, onNavigate }) => {
  const hasCritical   = site.siteStatus === 'critical';
  const hasCorr       = site.siteStatus === 'correlation';
  const isDegraded    = site.siteStatus === 'degraded';
  const isOffline     = site.siteStatus === 'offline';

  const cardBorder    = hasCorr ? 'border-orange-400 shadow-orange-100' : hasCritical ? 'border-red-300 shadow-red-100' : isDegraded ? 'border-amber-300' : isOffline ? 'border-slate-200' : 'border-slate-200 hover:border-cyan-300';
  const headerGrad    = hasCorr ? 'from-orange-500 to-amber-500' : hasCritical ? 'from-red-500 to-rose-500' : isDegraded ? 'from-amber-500 to-orange-400' : isOffline ? 'from-slate-500 to-slate-600' : 'from-slate-700 to-slate-800';

  return (
    <div className={`bg-white rounded-2xl border-2 ${cardBorder} shadow-lg overflow-hidden flex flex-col transition-all hover:shadow-xl cursor-pointer group`} onClick={() => onNavigate(site.name)}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${headerGrad} px-4 py-3.5`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-white text-base">🏢</span>
              <h3 className="text-white font-bold text-sm">{site.name}</h3>
              <RegionTag region={site.region} />
            </div>
            <div className="text-white/60 text-[10px]">{site.city}</div>
            {site.campusCode && <div className="text-white/40 text-[9px] font-mono mt-0.5">{site.campusCode}</div>}
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <StatusBadge status={site.siteStatus} />
            {site.correlationCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500 text-white rounded-full text-[9px] font-bold animate-pulse">
                🔗 {site.correlationCount} corr
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Agent health */}
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
        <AgentPill label="OT"  online={site.otOnline}  lastSeen={site.otLastHeartbeat}  colorClass="text-orange-700" />
        <AgentPill label="OOB" online={site.oobOnline} lastSeen={site.oobLastHeartbeat} colorClass="text-purple-700" notDeployed={!site.oobAgent} />
      </div>

      {/* Stats */}
      <div className="px-4 py-3 grid grid-cols-3 gap-2">
        <div className="text-center">
          <div className="text-base font-bold text-slate-800">{site.totalAssets}</div>
          <div className="text-[9px] text-slate-500 uppercase mt-0.5">Assets</div>
          <div className="text-[8px] text-slate-400 mt-0.5">{site.otAssetCount} OT · {site.oobAssetCount} OOB</div>
        </div>
        <div className="text-center">
          <div className={`text-base font-bold ${site.criticalCount > 0 ? 'text-red-600' : site.warningCount > 0 ? 'text-amber-600' : 'text-slate-800'}`}>{site.totalAlerts}</div>
          <div className="text-[9px] text-slate-500 uppercase mt-0.5">Alerts</div>
          <div className="text-[8px] mt-0.5">
            {site.criticalCount > 0 && <span className="text-red-500">{site.criticalCount} crit</span>}
            {site.criticalCount > 0 && site.warningCount > 0 && <span className="text-slate-300"> · </span>}
            {site.warningCount > 0 && <span className="text-amber-500">{site.warningCount} warn</span>}
            {site.totalAlerts === 0 && <span className="text-emerald-500">None</span>}
          </div>
        </div>
        <div className="text-center">
          <div className={`text-base font-bold ${site.highRiskAssets > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{site.highRiskAssets}</div>
          <div className="text-[9px] text-slate-500 uppercase mt-0.5">High Risk</div>
          <div className="text-[8px] text-slate-400 mt-0.5">of {site.totalAssets}</div>
        </div>
      </div>

      {/* Risk bar */}
      <div className="px-4 pb-2.5">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] text-slate-500 uppercase font-semibold">Risk Score</span>
          <RiskBadge score={site.riskScore} level={site.riskLevel} />
        </div>
        <RiskBar score={site.riskScore} />
      </div>

      <Corr004Panel
        siteName={site.name}
        corr004Data={site.corr004}
        loading={site.corr004Loading}
      />

      {/* Active protocols */}
      {site.protocols.length > 0 && (
        <div className="px-4 pb-2.5 flex flex-wrap gap-1">
          {site.protocols.map(p => (
            <span key={p} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[9px] font-medium rounded border border-slate-200">{p}</span>
          ))}
        </div>
      )}

      {/* Correlation preview */}
      {site.correlationCount > 0 && site.correlations?.[0] && (
        <div className="mx-4 mb-2.5 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-[10px]">🔗</span>
            <span className="text-[10px] font-semibold text-orange-700">{site.correlations[0].details?.correlation_rule_id}</span>
            <span className="text-[9px] text-orange-500">{site.correlations[0].details?.confidence}% conf</span>
          </div>
          <div className="text-[9px] text-orange-600 leading-relaxed line-clamp-2">{site.correlations[0].details?.description}</div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-auto px-4 py-2.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
        <div className="flex-1 min-w-0">
          {site.lastEvent ? (
            <>
              <div className="text-[9px] text-slate-400">Last event</div>
              <div className="text-[10px] text-slate-600 font-medium truncate">{site.lastEvent.description || site.lastEvent.event_type}</div>
            </>
          ) : (
            <div className="text-[10px] text-slate-400">No recent events</div>
          )}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onNavigate(site.name); }} className="ml-3 flex items-center gap-1 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold rounded-lg shrink-0">
          Open <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// SITE TABLE ROW
// ════════════════════════════════════════════════════════════════════════════

const SiteTableRow = ({ site, onNavigate }) => (
  <tr className="hover:bg-slate-50 cursor-pointer border-b border-slate-100" onClick={() => onNavigate(site.name)}>
    <td className="px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="text-base">🏢</span>
        <div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold text-slate-800">{site.name}</span>
            <RegionTag region={site.region} />
          </div>
          <div className="text-[10px] text-slate-400">{site.city}</div>
        </div>
      </div>
    </td>
    <td className="px-4 py-3"><StatusBadge status={site.siteStatus} /></td>
    <td className="px-4 py-3">
      <div className="flex items-center gap-1.5">
        <div className={`w-2 h-2 rounded-full ${site.otOnline ? 'bg-emerald-500 animate-pulse' : 'bg-red-400'}`} />
        <span className={`text-xs font-medium ${site.otOnline ? 'text-emerald-700' : 'text-red-600'}`}>{site.otOnline ? 'Online' : 'Offline'}</span>
      </div>
      {site.otLastHeartbeat && <div className="text-[9px] text-slate-400 mt-0.5">{site.otLastHeartbeat}</div>}
    </td>
    <td className="px-4 py-3">
      {site.oobAgent ? (
        <>
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${site.oobOnline ? 'bg-emerald-500 animate-pulse' : 'bg-red-400'}`} />
            <span className={`text-xs font-medium ${site.oobOnline ? 'text-emerald-700' : 'text-red-600'}`}>{site.oobOnline ? 'Online' : 'Offline'}</span>
          </div>
          {site.oobLastHeartbeat && <div className="text-[9px] text-slate-400 mt-0.5">{site.oobLastHeartbeat}</div>}
        </>
      ) : <span className="text-[10px] text-slate-400 italic">Not deployed</span>}
    </td>
    <td className="px-4 py-3 text-center">
      <div className="text-sm font-bold text-slate-700">{site.totalAssets}</div>
      <div className="text-[9px] text-slate-400">{site.otAssetCount} OT · {site.oobAssetCount} OOB</div>
    </td>
    <td className="px-4 py-3 text-center">
      {site.criticalCount > 0 ? (
        <div>
          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full border border-red-300">{site.criticalCount} critical</span>
          {site.warningCount > 0 && <div className="text-[9px] text-amber-600 mt-0.5">{site.warningCount} warning</div>}
        </div>
      ) : site.warningCount > 0 ? (
        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full border border-amber-300">{site.warningCount} warn</span>
      ) : <span className="text-xs text-emerald-600 font-medium">Clear</span>}
    </td>
    <td className="px-4 py-3 text-center">
      {site.correlationCount > 0
        ? <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded-full border border-orange-300 animate-pulse">🔗 {site.correlationCount}</span>
        : <span className="text-xs text-slate-400">—</span>}
    </td>
    <td className="px-4 py-3">
      <div className="flex items-center gap-2 min-w-[120px]">
        <RiskBar score={site.riskScore} />
        <span className={`text-xs font-bold whitespace-nowrap ${site.riskLevel === 'Critical' ? 'text-red-600' : site.riskLevel === 'High' ? 'text-amber-600' : site.riskLevel === 'Medium' ? 'text-yellow-600' : 'text-emerald-600'}`}>{site.riskScore}</span>
      </div>
    </td>
    <td className="px-4 py-3">
      {site.lastEvent
        ? <div><div className="text-xs text-slate-600 truncate max-w-[180px]">{site.lastEvent.description || site.lastEvent.event_type}</div><div className="text-[9px] text-slate-400 mt-0.5">{site.lastEvent.timestamp ? new Date(site.lastEvent.timestamp).toLocaleString() : ''}</div></div>
        : <span className="text-xs text-slate-400">No events</span>}
    </td>
    <td className="px-4 py-3">
      <button onClick={(e) => { e.stopPropagation(); onNavigate(site.name); }} className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold rounded-lg whitespace-nowrap">Open →</button>
    </td>
  </tr>
);

// ════════════════════════════════════════════════════════════════════════════
// FLEET SUMMARY BAR
// ════════════════════════════════════════════════════════════════════════════

const FleetSummaryBar = ({ sites }) => {
  const total         = sites.length;
  const agentsOnline  = sites.reduce((n,s) => n + (s.otOnline ? 1:0) + (s.oobOnline ? 1:0), 0);
  const agentsTotal   = sites.reduce((n,s) => n + (s.otAgent ? 1:0) + (s.oobAgent ? 1:0), 0);
  const totalAssets   = sites.reduce((n,s) => n + s.totalAssets, 0);
  const critAlerts    = sites.reduce((n,s) => n + s.criticalCount, 0);
  const totalCorr     = sites.reduce((n,s) => n + s.correlationCount, 0);
  const degraded      = sites.filter(s => ['degraded','offline'].includes(s.siteStatus)).length;
  const usCount       = sites.filter(s => s.region === 'US').length;
  const latamCount    = sites.filter(s => s.region === 'LATAM').length;

  const tiles = [
    { label: 'Sites',          value: total,                   sub: `${usCount} US · ${latamCount} LATAM`,    color: 'text-slate-800',  bg: 'bg-white border-slate-200' },
    { label: 'Agents Online',  value: `${agentsOnline}/${agentsTotal}`, sub: 'OT + OOB pairs',               color: agentsOnline === agentsTotal ? 'text-emerald-600' : 'text-amber-600', bg: 'bg-white border-slate-200' },
    { label: 'DC Assets',      value: totalAssets,             sub: 'PDU · CRAC · UPS · BMC',                color: 'text-blue-600',   bg: 'bg-white border-slate-200' },
    { label: 'Critical Alerts',value: critAlerts,              sub: critAlerts > 0 ? 'Needs attention' : 'All clear', color: critAlerts > 0 ? 'text-red-600' : 'text-emerald-600', bg: critAlerts > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200' },
    { label: 'Correlations',   value: totalCorr,               sub: 'Cross-network',                         color: totalCorr > 0 ? 'text-orange-600' : 'text-slate-400', bg: totalCorr > 0 ? 'bg-orange-50 border-orange-200' : 'bg-white border-slate-200' },
    { label: 'Degraded / Down',value: degraded,                sub: 'Require attention',                     color: degraded > 0 ? 'text-amber-600' : 'text-slate-400', bg: 'bg-white border-slate-200' },
  ];

  return (
    <div className="grid grid-cols-6 gap-3 mb-6">
      {tiles.map(t => (
        <div key={t.label} className={`${t.bg} border rounded-xl px-4 py-3 shadow-sm`}>
          <div className={`text-2xl font-bold ${t.color}`}>{t.value}</div>
          <div className="text-xs font-medium text-slate-600 mt-0.5">{t.label}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">{t.sub}</div>
        </div>
      ))}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const DCFleet = () => {
  const navigate = useNavigate();

  const [loading, setLoading]             = useState(true);
  const [isRefreshing, setIsRefreshing]   = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const [useMock, setUseMock]   = useState(false);
  const [liveSites, setLiveSites] = useState([]);

  const [searchQuery, setSearchQuery]   = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [sortBy, setSortBy]             = useState('risk');
  const [viewMode, setViewMode]         = useState('table');

  const [corr004, setCorr004] = useState({});
  const [corr004Loading, setCorr004Loading] = useState(true);

  const fetchData = useCallback(async (manual = false) => {
    if (manual) setIsRefreshing(true);
    try {
      const [enr, evr] = await Promise.all([edgeNodesService.getEdgeNodes(), eventsService.getEvents({ limit: 500 })]);
      const edgeNodes = enr.data || enr || [];
      const events    = evr.data || evr || [];
      const dcAgents  = edgeNodes.filter(n => n.edge_node_id?.toUpperCase().startsWith('DC_'));

      if (dcAgents.length > 0) {
        const sites = groupBySite(dcAgents);
        const corr  = events.filter(e => e.event_type?.startsWith('CorrelationAlert_'));
        setLiveSites(Object.values(sites).map(s => buildLiveSiteData(s, events, corr)));
        setUseMock(false);
      } else {
        setUseMock(true);
        setLiveSites([]);
      }
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('DCFleet fetch error:', err);
      setUseMock(true);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  const fetchCorr004 = useCallback(async () => {
    try {
      const base = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${base}/admin/ingest/corr004/results`);
      if (!res.ok) return;
      const data = await res.json();
      const indexed = {};
      for (const row of data.results || []) {
        if (!indexed[row.edge_node_id]) indexed[row.edge_node_id] = {};
        indexed[row.edge_node_id][row.correlator] = row;
      }
      setCorr004(indexed);
    } catch (e) {
      console.warn('CORR-004 fetch failed:', e);
    } finally {
      setCorr004Loading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchCorr004();
    const interval = setInterval(() => {
      fetchData();
      fetchCorr004();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchData, fetchCorr004]);

  const allSites = useMemo(() => {
    const sites = useMock
      ? MOCK_ALIGNED_SITES.map(buildMockSiteData)
      : liveSites;
    return sites.map(site => ({
      ...site,
      corr004: corr004,
      corr004Loading: corr004Loading,
    }));
  }, [useMock, liveSites, corr004, corr004Loading]);

  const filteredSites = useMemo(() => {
    let r = allSites;
    if (searchQuery) { const q = searchQuery.toLowerCase(); r = r.filter(s => s.name.toLowerCase().includes(q) || (s.city||'').toLowerCase().includes(q) || (s.campusCode||'').toLowerCase().includes(q)); }
    if (regionFilter !== 'all') r = r.filter(s => s.region === regionFilter);
    if (statusFilter === 'alert')    r = r.filter(s => s.criticalCount > 0 || s.correlationCount > 0);
    else if (statusFilter === 'online')   r = r.filter(s => s.otOnline || s.oobOnline);
    else if (statusFilter === 'offline')  r = r.filter(s => !s.otOnline && !s.oobOnline);
    else if (statusFilter === 'degraded') r = r.filter(s => s.siteStatus === 'degraded');
    const sortFns = {
      risk:   (a,b) => b.riskScore - a.riskScore,
      name:   (a,b) => a.name.localeCompare(b.name),
      alerts: (a,b) => (b.criticalCount+b.warningCount)-(a.criticalCount+a.warningCount),
      status: (a,b) => { const o={correlation:0,critical:1,degraded:2,warning:3,offline:4,healthy:5,unconfigured:6}; return (o[a.siteStatus]??9)-(o[b.siteStatus]??9); },
    };
    return [...r].sort(sortFns[sortBy] || sortFns.risk);
  }, [allSites, searchQuery, statusFilter, regionFilter, sortBy]);

  const handleNavigateToSite = useCallback((siteName) => {
    try { localStorage.setItem('projectOracleSelectedSite', siteName); } catch {}
    navigate('/project-oracle');
  }, [navigate]);

  if (loading) {
    return (
      <div className="h-[calc(100vh-64px)] w-full flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-14 h-14 border-4 border-cyan-200 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4" />
          <div className="text-cyan-600 text-base font-medium">Loading DC Fleet...</div>
          <div className="text-slate-400 text-xs mt-1">Fetching agent registry</div>
        </div>
      </div>
    );
  }

  const hasRegions = [...new Set(allSites.map(s => s.region))].length > 1;

  return (
    <div className="min-h-[calc(100vh-64px)] w-full bg-white">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-8 py-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl flex items-center justify-center border border-cyan-300">
              <span className="text-xl">🏢</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-slate-800">DC Fleet</h1>
                {useMock && <span className="px-2 py-0.5 bg-amber-50 border border-amber-200 rounded-full text-[10px] text-amber-700 font-semibold">Demo · Aligned Data Centers</span>}
              </div>
              <p className="text-slate-500 text-xs mt-0.5">
                Multi-site data center overview · {allSites.length} sites
                {lastRefreshed && <span className="text-slate-400"> · Updated {lastRefreshed.toLocaleTimeString()}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => fetchData(true)} disabled={isRefreshing} className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 shadow-sm">
              <svg className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <div className="flex items-center bg-slate-100 rounded-lg p-1">
              <button onClick={() => setViewMode('grid')}  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode==='grid'  ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>⊞ Grid</button>
              <button onClick={() => setViewMode('table')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode==='table' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>☰ Table</button>
            </div>
            <button onClick={() => navigate('/project-oracle')} className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold rounded-lg shadow-sm">
              <span>🔮</span> Project Flow
            </button>
          </div>
        </div>
      </div>

      <div className="px-8 py-6">
        <FleetSummaryBar sites={allSites} />

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search sites, cities, campus codes..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-cyan-500" />
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            {[{id:'all',label:'All'},{id:'alert',label:'🚨 Alerts'},{id:'online',label:'● Online'},{id:'degraded',label:'⚠ Degraded'},{id:'offline',label:'○ Offline'}].map(f => (
              <button key={f.id} onClick={() => setStatusFilter(f.id)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${statusFilter===f.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{f.label}</button>
            ))}
          </div>
          {hasRegions && (
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              {[{id:'all',label:'All Regions'},{id:'US',label:'🇺🇸 US'},{id:'LATAM',label:'🌎 LATAM'}].map(f => (
                <button key={f.id} onClick={() => setRegionFilter(f.id)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${regionFilter===f.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{f.label}</button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Sort by</span>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-cyan-500 text-slate-700">
              <option value="risk">Risk Score</option>
              <option value="status">Status</option>
              <option value="alerts">Alerts</option>
              <option value="name">Name</option>
            </select>
          </div>
          <div className="ml-auto text-xs text-slate-400">{filteredSites.length} of {allSites.length} sites</div>
        </div>

        {/* Grid */}
        {viewMode === 'grid' && (
          filteredSites.length === 0
            ? <div className="text-center py-16"><span className="text-4xl">🔍</span><p className="text-slate-600 font-medium mt-3">No sites match your filters</p><button onClick={() => { setSearchQuery(''); setStatusFilter('all'); setRegionFilter('all'); }} className="mt-3 text-cyan-600 text-sm">Clear filters</button></div>
            : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">{filteredSites.map(site => <SiteCard key={site.name} site={site} onNavigate={handleNavigateToSite} />)}</div>
        )}

        {/* Table */}
        {viewMode === 'table' && (
          filteredSites.length === 0
            ? <div className="text-center py-16"><span className="text-4xl">🔍</span><p className="text-slate-600 font-medium mt-3">No sites match your filters</p><button onClick={() => { setSearchQuery(''); setStatusFilter('all'); setRegionFilter('all'); }} className="mt-3 text-cyan-600 text-sm">Clear filters</button></div>
            : (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      {['Site','Status','OT Agent','OOB Agent','Assets','Alerts','Correlations','Risk','Last Event',''].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{filteredSites.map(site => <SiteTableRow key={site.name} site={site} onNavigate={handleNavigateToSite} />)}</tbody>
                </table>
              </div>
            )
        )}

        {/* Footer */}
        <div className="mt-8 pt-5 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gradient-to-br from-cyan-500 to-blue-500 rounded flex items-center justify-center"><span className="text-[9px] font-bold text-white">e</span></div>
            Powered by <span className="text-cyan-600 font-semibold ml-1">AI Sentry DC Fleet</span>
            {useMock && <span className="ml-2 text-amber-500">· Demo · Aligned Data Centers portfolio</span>}
          </div>
          <div>Polling every 60s · {allSites.length} sites monitored</div>
        </div>
      </div>
    </div>
  );
};

export default DCFleet;
